import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api, { getImageUrl } from '../../services/api';
import { Plus, Edit, Trash2, Search, Filter, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ModalMultiplosSucos from '../../components/ModalMultiplosSucos';
import './Cardapio.css';

const Cardapio = () => {
  const [busca, setBusca] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [modalAberto, setModalAberto] = useState(false);
  const [modalSucosAberto, setModalSucosAberto] = useState(false);
  const [produtoEditando, setProdutoEditando] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    ['produtos', busca, categoriaFiltro],
    async () => {
      const params = new URLSearchParams();
      if (busca) params.append('busca', busca);
      if (categoriaFiltro) params.append('categoria', categoriaFiltro);
      const response = await api.get(`/api/produtos?${params}`);
      return response.data.data.produtos;
    }
  );

  const categorias = [...new Set(data?.map(p => p.categoria) || [])];

  const deleteMutation = useMutation(
    (id) => api.delete(`/api/produtos/${id}`),
    {
      onSuccess: () => {
        toast.success('Produto removido!');
        queryClient.invalidateQueries('produtos');
      },
      onError: () => toast.error('Erro ao remover produto')
    }
  );

  if (isLoading) {
    return <div className="loading">Carregando cardápio...</div>;
  }

  return (
    <div className="cardapio-page">
      <div className="page-header">
        <div>
          <h2>Cardápio</h2>
          <p>Gerencie seus produtos e categorias</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => setModalSucosAberto(true)} 
            style={{ background: '#0ea5e9' }} 
          >
            🍹 Criar Múltiplos Sucos
          </button>
          <button className="btn btn-primary" onClick={() => setModalAberto(true)}>
            <Plus size={20} />
            Novo Produto
          </button>
        </div>
      </div>

      <div className="filtros-cardapio">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar produto..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="input-search"
          />
        </div>
        <div className="filter-box">
          <Filter size={20} />
          <select
            value={categoriaFiltro}
            onChange={(e) => setCategoriaFiltro(e.target.value)}
            className="select-filter"
          >
            <option value="">Todas as categorias</option>
            {categorias.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="categorias-grid">
        {categorias.map(categoria => {
          const produtosCategoria = data?.filter(p => p.categoria === categoria) || [];
          return (
            <div key={categoria} className="categoria-section">
              <h3 className="categoria-title">{categoria}</h3>
              <div className="produtos-grid">
                {produtosCategoria.map(produto => (
                  <div key={produto._id} className="produto-card">
                    {/* ✅ Adicionar exibição de imagem no cardápio do admin */}
                    <div className="produto-imagem">
                      {produto.imagens && produto.imagens.length > 0 && produto.imagens[0]?.url ? (
                        <img 
                          src={getImageUrl(produto.imagens[0].url)} 
                          alt={produto.nome}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const parent = e.target.parentElement;
                            if (parent) {
                              const placeholder = parent.querySelector('.produto-placeholder');
                              if (placeholder) {
                                placeholder.style.display = 'flex';
                              }
                            }
                          }}
                        />
                      ) : null}
                      {(!produto.imagens || produto.imagens.length === 0 || !produto.imagens[0]?.url) && (
                        <div className="produto-placeholder">
                          <span>📷</span>
                        </div>
                      )}
                    </div>
                    <div className="produto-header">
                      <h4>{produto.nome}</h4>
                      <div className="produto-actions">
                        <button
                          className="btn-icon"
                          onClick={() => {
                            setProdutoEditando(produto);
                            setModalAberto(true);
                          }}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={() => {
                            if (window.confirm('Deseja remover este produto?')) {
                              deleteMutation.mutate(produto._id);
                            }
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="produto-descricao">{produto.descricao}</p>
                    <div className="produto-footer">
                      <span className="produto-preco">R$ {produto.preco?.toFixed(2)}</span>
                      {produto.precoPromocional && (
                        <span className="produto-preco-promo">
                          R$ {produto.precoPromocional.toFixed(2)}
                        </span>
                      )}
                      <span className={`produto-status ${produto.disponivel ? 'disponivel' : 'indisponivel'}`}>
                        {produto.disponivel ? 'Disponível' : 'Indisponível'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {modalAberto && (
        <ModalProduto
          produto={produtoEditando}
          onClose={() => {
            setModalAberto(false);
            setProdutoEditando(null);
          }}
        />
      )}

      {modalSucosAberto && (
        <ModalMultiplosSucos
          onClose={() => setModalSucosAberto(false)}
          onSuccess={() => {
            queryClient.invalidateQueries('produtos');
            setModalSucosAberto(false);
          }}
        />
      )}
    </div>
  );
};

const ModalProduto = ({ produto, onClose }) => {
  const [formData, setFormData] = useState({
    nome: produto?.nome || '',
    descricao: produto?.descricao || '',
    preco: produto?.preco || '',
    precoPromocional: produto?.precoPromocional || '',
    categoria: produto?.categoria || '',
    estoque: produto?.estoque?.quantidade || 0,
    disponivel: produto?.disponivel !== false,
    aceitaCrediario: produto?.aceitaCrediario === true,
    adicionais: Array.isArray(produto?.adicionais) ? produto.adicionais : [],
    temVariacoes: produto?.temVariacoes || false,
    variacoes: produto?.variacoes || []
  });

  const [imagemPreview, setImagemPreview] = useState(() => {
    const urlOriginal = produto?.imagens?.[0]?.url || produto?.imagens?.[0]?.medium || null;
    if (urlOriginal) {
      return getImageUrl(urlOriginal);
    }
    return null;
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [modoSucos, setModoSucos] = useState(false); // Modo especial para inserir múltiplos sucos
  const [saboresSucos, setSaboresSucos] = useState(''); // Texto com sabores separados por vírgula
  
  // Atualizar preview quando produto mudar
  useEffect(() => {
    const urlOriginal = produto?.imagens?.[0]?.url || produto?.imagens?.[0]?.medium || null;
    if (urlOriginal) {
      const urlCompleta = getImageUrl(urlOriginal);
      console.log('📸 Atualizando preview do produto:', { urlOriginal, urlCompleta });
      setImagemPreview(urlCompleta);
    } else {
      setImagemPreview(null);
    }
  }, [produto]);

  const queryClient = useQueryClient();
  
  // Buscar estabelecimento de forma confiável
  const { data: estabelecimentoData } = useQuery(
    'estabelecimento-produto',
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        return response.data.data.estabelecimento;
      } catch (error) {
        // Se não encontrar estabelecimento do usuário, buscar o padrão
        if (error.response?.status === 404) {
          try {
            const response = await api.get('/api/estabelecimentos/padrao');
            return response.data.data.estabelecimento;
          } catch (padraoError) {
            console.error('Erro ao buscar estabelecimento padrão:', padraoError);
            return null;
          }
        }
        throw error;
      }
    },
    {
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

  const estabelecimento = estabelecimentoData?._id;

  // Buscar categorias do estabelecimento
  const { data: categoriasData } = useQuery(
    ['categorias', estabelecimento],
    async () => {
      if (!estabelecimento) return [];
      const response = await api.get(`/api/categorias?estabelecimento=${estabelecimento}&ativa=true`);
      return response.data.data.categorias || [];
    },
    {
      enabled: !!estabelecimento,
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

  const categorias = categoriasData || [];

  const saveMutation = useMutation(
    async (data) => {
      if (produto) {
        return api.put(`/api/produtos/${produto._id}`, data);
      }
      
      // Validar estabelecimento antes de criar
      if (!estabelecimento) {
        throw new Error('Estabelecimento não encontrado. Por favor, configure um estabelecimento primeiro.');
      }
      
      return api.post('/api/produtos', { ...data, estabelecimento });
    },
    {
      onSuccess: () => {
        toast.success(produto ? 'Produto atualizado!' : 'Produto criado!');
        queryClient.invalidateQueries('produtos');
        onClose();
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar produto';
        const errors = error.response?.data?.errors;
        
        if (errors && Array.isArray(errors)) {
          // Mostrar erros de validação
          errors.forEach(err => {
            toast.error(err.msg || err.message || 'Erro de validação');
          });
        } else {
          toast.error(errorMessage);
        }
        console.error('Erro ao salvar produto:', error);
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Preparar dados para envio (converter preço para número)
    const dadosEnvio = {
      ...formData,
      preco: formData.temVariacoes ? (parseFloat(formData.preco) || 0) : (parseFloat(formData.preco) || 0), // Preço base (pode ser usado como referência)
      precoPromocional: formData.precoPromocional ? parseFloat(formData.precoPromocional) : undefined,
      estoque: {
        quantidade: parseInt(formData.estoque) || 0
      },
      aceitaCrediario: formData.aceitaCrediario === true,
      adicionais: Array.isArray(formData.adicionais)
        ? formData.adicionais
            .filter((a) => a && String(a.nome || '').trim() !== '')
            .map((a) => ({
              nome: String(a.nome || '').trim(),
              preco: Number(a.preco) || 0,
              disponivel: a?.disponivel !== false
            }))
        : [],
      temVariacoes: formData.temVariacoes || false,
      variacoes: formData.temVariacoes ? formData.variacoes.filter(v => v.nome && v.preco > 0) : []
    };
    
    // Validar campos obrigatórios antes de enviar
    if (!dadosEnvio.nome || dadosEnvio.nome.trim() === '') {
      toast.error('Nome do produto é obrigatório');
      return;
    }
    
    if (!dadosEnvio.temVariacoes && (!dadosEnvio.preco || dadosEnvio.preco <= 0)) {
      toast.error('Preço deve ser maior que zero');
      return;
    }
    
    if (dadosEnvio.temVariacoes && dadosEnvio.variacoes.length === 0) {
      toast.error('Adicione pelo menos uma variação');
      return;
    }
    
    if (!dadosEnvio.categoria || dadosEnvio.categoria.trim() === '') {
      toast.error('Categoria é obrigatória');
      return;
    }
    
    saveMutation.mutate(dadosEnvio);
  };

  // Função para fazer upload de imagem
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.match('image.*')) {
      toast.error('Apenas imagens são permitidas!');
      return;
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande! Máximo 5MB');
      return;
    }

    if (!produto || !produto._id) {
      toast.error('Salve o produto primeiro antes de adicionar imagem');
      return;
    }

    setUploadingImage(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('imagem', file);

      const token = localStorage.getItem('token');
      // Em produção, usar window.location.origin (mesmo domínio)
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? window.location.origin 
        : (process.env.REACT_APP_API_URL || 'http://localhost:5000');
      
      const response = await fetch(`${apiUrl}/api/produtos/${produto._id}/imagem`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Imagem enviada e redimensionada com sucesso!');
        console.log('📸 Resposta do upload:', data);
        console.log('📸 Produto retornado:', data.data.produto);
        console.log('📸 Imagens do produto:', data.data.produto?.imagens);
        
        // O backend retorna produto.imagens como array [{url, principal}]
        console.log('📸 Estrutura completa do produto:', JSON.stringify(data.data.produto, null, 2));
        console.log('📸 Tipo de imagens:', typeof data.data.produto?.imagens);
        console.log('📸 É array?', Array.isArray(data.data.produto?.imagens));
        console.log('📸 Tamanho do array:', data.data.produto?.imagens?.length);
        
        // ✅ Tentar múltiplas formas de extrair a URL
        let imagemPrincipal = null;
        
        // Tentar 1: imagens[0].url
        if (data.data.produto?.imagens?.[0]?.url) {
          imagemPrincipal = data.data.produto.imagens[0].url;
          console.log('✅ URL encontrada via imagens[0].url:', imagemPrincipal);
        }
        // Tentar 2: imagens[0] (se for string direta)
        else if (data.data.produto?.imagens?.[0] && typeof data.data.produto.imagens[0] === 'string') {
          imagemPrincipal = data.data.produto.imagens[0];
          console.log('✅ URL encontrada via imagens[0] (string):', imagemPrincipal);
        }
        // Tentar 3: imagens.url (se for objeto único)
        else if (data.data.produto?.imagens?.url) {
          imagemPrincipal = data.data.produto.imagens.url;
          console.log('✅ URL encontrada via imagens.url:', imagemPrincipal);
        }
        // Tentar 4: imagens (se for string direta)
        else if (typeof data.data.produto?.imagens === 'string') {
          imagemPrincipal = data.data.produto.imagens;
          console.log('✅ URL encontrada via imagens (string):', imagemPrincipal);
        }
        
        console.log('📸 URL principal extraída (final):', imagemPrincipal);
        
        if (imagemPrincipal) {
          const urlCompleta = getImageUrl(imagemPrincipal);
          console.log('📸 URL completa construída:', urlCompleta);
          setImagemPreview(urlCompleta);
        } else {
          console.error('❌ Nenhuma imagem encontrada no produto retornado!');
          console.error('❌ Estrutura completa:', JSON.stringify(data.data.produto, null, 2));
          console.error('❌ data.data.produto.imagens:', data.data.produto?.imagens);
          console.error('❌ data.data.produto.imagens[0]:', data.data.produto?.imagens?.[0]);
          console.error('❌ data.data.produto.imagens[0] completo:', JSON.stringify(data.data.produto?.imagens?.[0], null, 2));
        }
        // ✅ Invalidar queries para atualizar lista e cardápio do cliente
        queryClient.invalidateQueries('produtos');
      } else {
        toast.error(data.message || 'Erro ao enviar imagem');
      }
    } catch (error) {
      toast.error('Erro ao fazer upload da imagem');
      console.error(error);
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>{produto ? 'Editar' : 'Novo'} Produto</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nome</label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Descrição</label>
            <textarea
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              rows={3}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Preço</label>
              <input
                type="number"
                step="0.01"
                value={formData.preco}
                onChange={(e) => setFormData({ ...formData, preco: parseFloat(e.target.value) })}
                required
              />
            </div>
            <div className="form-group">
              <label>Preço Promocional</label>
              <input
                type="number"
                step="0.01"
                value={formData.precoPromocional}
                onChange={(e) => setFormData({ ...formData, precoPromocional: parseFloat(e.target.value) || '' })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Categoria *</label>
              <select
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid var(--gray-300)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem'
                }}
              >
                <option value="">Selecione uma categoria</option>
                {categorias.map(cat => (
                  <option key={cat._id} value={cat.nome}>
                    {cat.nome}
                  </option>
                ))}
              </select>
              {categorias.length === 0 && (
                <small style={{ color: '#ef4444', display: 'block', marginTop: '0.5rem' }}>
                  ⚠️ Nenhuma categoria cadastrada. <a href="/admin/categorias" style={{ color: 'var(--primary)' }}>Cadastre uma categoria primeiro</a>
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Estoque</label>
              <input
                type="number"
                value={formData.estoque}
                onChange={(e) => setFormData({ ...formData, estoque: parseInt(e.target.value) })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.disponivel}
                onChange={(e) => setFormData({ ...formData, disponivel: e.target.checked })}
              />
              {' '}Disponível
            </label>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.aceitaCrediario === true}
                onChange={(e) => setFormData({ ...formData, aceitaCrediario: e.target.checked })}
              />
              {' '}Aceita Crediário
            </label>
          </div>

          <div style={{
            padding: '1.5rem',
            background: '#f9fafb',
            borderRadius: '0.5rem',
            marginTop: '1rem',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ margin: 0 }}>Guarnições (Marmitex)</h4>
              <button
                type="button"
                onClick={() => {
                  const adicionaisAtual = Array.isArray(formData.adicionais) ? formData.adicionais : [];
                  setFormData({
                    ...formData,
                    adicionais: [...adicionaisAtual, { nome: '', preco: 0, disponivel: true }]
                  });
                }}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                + Adicionar guarnição
              </button>
            </div>

            {(Array.isArray(formData.adicionais) ? formData.adicionais : []).map((adicional, index) => (
              <div
                key={index}
                style={{
                  padding: '1rem',
                  background: 'white',
                  borderRadius: '0.5rem',
                  marginBottom: '0.5rem',
                  border: '1px solid #e5e7eb'
                }}
              >
                <div className="form-row">
                  <div className="form-group">
                    <label>Nome</label>
                    <input
                      type="text"
                      value={adicional?.nome || ''}
                      onChange={(e) => {
                        const adicionaisAtual = Array.isArray(formData.adicionais) ? [...formData.adicionais] : [];
                        adicionaisAtual[index] = { ...adicionaisAtual[index], nome: e.target.value };
                        setFormData({ ...formData, adicionais: adicionaisAtual });
                      }}
                      placeholder="Ex: Arroz, Feijão, Macarrão"
                    />
                  </div>
                  <div className="form-group">
                    <label>Preço (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={Number(adicional?.preco || 0)}
                      onChange={(e) => {
                        const adicionaisAtual = Array.isArray(formData.adicionais) ? [...formData.adicionais] : [];
                        adicionaisAtual[index] = { ...adicionaisAtual[index], preco: parseFloat(e.target.value) || 0 };
                        setFormData({ ...formData, adicionais: adicionaisAtual });
                      }}
                    />
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <label style={{ marginBottom: 0 }}>
                      <input
                        type="checkbox"
                        checked={adicional?.disponivel !== false}
                        onChange={(e) => {
                          const adicionaisAtual = Array.isArray(formData.adicionais) ? [...formData.adicionais] : [];
                          adicionaisAtual[index] = { ...adicionaisAtual[index], disponivel: e.target.checked };
                          setFormData({ ...formData, adicionais: adicionaisAtual });
                        }}
                      />
                      {' '}Disponível
                    </label>
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const adicionaisAtual = Array.isArray(formData.adicionais) ? formData.adicionais : [];
                        setFormData({ ...formData, adicionais: adicionaisAtual.filter((_, i) => i !== index) });
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        cursor: 'pointer'
                      }}
                    >
                      Remover
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Modo Especial para Sucos */}
          {formData.categoria === 'SUCOS' && (
            <div style={{ 
              padding: '1.5rem', 
              background: '#e0f2fe', 
              borderRadius: '0.5rem', 
              marginTop: '1rem',
              border: '2px solid #0ea5e9'
            }}>
              <h4 style={{ marginBottom: '1rem', color: '#0369a1' }}>🍹 Modo Rápido para Sucos</h4>
              <p style={{ marginBottom: '1rem', color: '#0c4a6e', fontSize: '0.9rem' }}>
                Insira os sabores separados por vírgula. Cada sabor será criado com os tamanhos 500ml e Jarra 1L.
              </p>
              <div className="form-group">
                <label>Sabores (separados por vírgula)</label>
                <textarea
                  value={saboresSucos}
                  onChange={(e) => setSaboresSucos(e.target.value)}
                  placeholder="Ex: Laranja, Maracujá, Acerola, Limão, Abacaxi, Manga"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #0ea5e9',
                    borderRadius: '0.5rem',
                    fontSize: '1rem'
                  }}
                />
                <small style={{ color: '#0369a1', display: 'block', marginTop: '0.5rem' }}>
                  Exemplo: Laranja, Maracujá, Acerola, Limão
                </small>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Preço 500ml (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.preco || ''}
                    onChange={(e) => setFormData({ ...formData, preco: parseFloat(e.target.value) || '' })}
                    placeholder="6.00"
                  />
                </div>
                <div className="form-group">
                  <label>Preço Jarra 1L (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    id="precoJarra"
                    placeholder="10.00"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!saboresSucos.trim()) {
                    toast.error('Digite pelo menos um sabor');
                    return;
                  }
                  const preco500ml = parseFloat(formData.preco) || 0;
                  const precoJarra = parseFloat(document.getElementById('precoJarra')?.value) || 0;
                  
                  if (!preco500ml || !precoJarra) {
                    toast.error('Preencha os preços de ambos os tamanhos');
                    return;
                  }

                  const sabores = saboresSucos.split(',').map(s => s.trim()).filter(s => s);
                  const novasVariacoes = [];
                  
                  sabores.forEach(sabor => {
                    novasVariacoes.push({
                      nome: `${sabor} - 500ml`,
                      preco: preco500ml,
                      disponivel: true,
                      ordem: 0
                    });
                    novasVariacoes.push({
                      nome: `${sabor} - Jarra 1L`,
                      preco: precoJarra,
                      disponivel: true,
                      ordem: 1
                    });
                  });

                  setFormData({
                    ...formData,
                    temVariacoes: true,
                    variacoes: novasVariacoes,
                    nome: 'SUCO' // Nome genérico, será substituído pelo sabor na variação
                  });
                  
                  toast.success(`${sabores.length} sabores criados com ${novasVariacoes.length} variações!`);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#0ea5e9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginTop: '0.5rem'
                }}
              >
                ✅ Criar Variações dos Sabores
              </button>
            </div>
          )}

          {/* Gerenciar Variações Manualmente */}
          <div style={{ 
            padding: '1.5rem', 
            background: '#f9fafb', 
            borderRadius: '0.5rem', 
            marginTop: '1rem',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ margin: 0 }}>📦 Variações de Tamanho/Opções</h4>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.temVariacoes}
                  onChange={(e) => setFormData({ ...formData, temVariacoes: e.target.checked })}
                />
                <span>Produto tem variações</span>
              </label>
            </div>

            {formData.temVariacoes && (
              <>
                {formData.variacoes.map((variacao, index) => (
                  <div key={index} style={{ 
                    padding: '1rem', 
                    background: 'white', 
                    borderRadius: '0.5rem', 
                    marginBottom: '0.5rem',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Nome da Variação</label>
                        <input
                          type="text"
                          value={variacao.nome}
                          onChange={(e) => {
                            const novasVariacoes = [...formData.variacoes];
                            novasVariacoes[index].nome = e.target.value;
                            setFormData({ ...formData, variacoes: novasVariacoes });
                          }}
                          placeholder="Ex: 500ml, Jarra 1L"
                        />
                      </div>
                      <div className="form-group">
                        <label>Preço (R$)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={variacao.preco}
                          onChange={(e) => {
                            const novasVariacoes = [...formData.variacoes];
                            novasVariacoes[index].preco = parseFloat(e.target.value) || 0;
                            setFormData({ ...formData, variacoes: novasVariacoes });
                          }}
                        />
                      </div>
                      <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => {
                            const novasVariacoes = formData.variacoes.filter((_, i) => i !== index);
                            setFormData({ ...formData, variacoes: novasVariacoes });
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            cursor: 'pointer'
                          }}
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      variacoes: [...formData.variacoes, { nome: '', preco: 0, disponivel: true, ordem: formData.variacoes.length }]
                    });
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontWeight: '600',
                    marginTop: '0.5rem'
                  }}
                >
                  + Adicionar Variação
                </button>
              </>
            )}
          </div>
          
          {/* Campo de Upload de Imagem */}
          {produto && produto._id && (
            <div className="form-group">
              <label>Imagem do Produto</label>
              {imagemPreview && (
                <div style={{ 
                  marginBottom: '1rem', 
                  position: 'relative', 
                  display: 'inline-block',
                  borderRadius: '0.5rem',
                  overflow: 'hidden',
                  border: '2px solid #e5e7eb'
                }}>
                  <img 
                    src={imagemPreview}
                    alt="Preview"
                    onLoad={() => console.log('✅ Preview da imagem carregado:', imagemPreview)}
                    onError={(e) => {
                      console.error('❌ Erro ao carregar preview:', imagemPreview);
                      console.error('❌ Erro:', e);
                    }}
                    style={{ 
                      width: '200px', 
                      height: '200px', 
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                </div>
              )}
              <div style={{
                padding: '1rem',
                border: '2px dashed #d1d5db',
                borderRadius: '0.5rem',
                textAlign: 'center',
                backgroundColor: uploadingImage ? '#f3f4f6' : '#ffffff',
                cursor: uploadingImage ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  style={{ 
                    display: 'none'
                  }}
                  id="imagem-upload"
                />
                <label 
                  htmlFor="imagem-upload"
                  style={{
                    cursor: uploadingImage ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Upload size={24} color={uploadingImage ? '#9ca3af' : '#667eea'} />
                  <span style={{ color: uploadingImage ? '#9ca3af' : '#374151', fontSize: '0.875rem' }}>
                    {uploadingImage ? 'Enviando...' : 'Clique para fazer upload'}
                  </span>
                </label>
              </div>
              {uploadingImage && (
                <p style={{ color: '#667eea', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                  ⏳ Enviando e redimensionando imagem...
                </p>
              )}
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280', fontSize: '0.75rem' }}>
                A imagem será redimensionada automaticamente (Thumbnail, Médio, Grande)
              </small>
            </div>
          )}
          
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              {produto ? 'Atualizar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Cardapio;

